import _ from 'lodash'
import { getStyleOptionsByKind } from 'shared-js/menu-services/department-styles-service'
import { getItemGroupSingleDepartmentId } from 'shared-js/menu-services/offers-service'
import {
  availableCategoriesIds,
  availableItemsIds
} from 'shared-js/references/menu/department/clean-department-utils'

export function cleanOfferValue (offer, menuBlob) {
  if (_.isEmpty(offer.itemGroups) && !['ENTIRE_ORDER', 'ANY_ITEM'].includes(offer.value.appliesTo)) {
    offer.value.appliesTo = 'ENTIRE_ORDER'
  }

  _.forEachRight(_.get(offer, 'value.pricingSchemes', []), scheme => {
    if (!_.get(menuBlob, 'pricingSchemes', []).find(it => it.id === scheme.schemeId)) {
      return offer.value.pricingSchemes.splice(offer.value.pricingSchemes.indexOf(scheme), 1)
    }

    cleanOfferValueIndividualGroups(scheme.individualGroups, offer, menuBlob)
    cleanOfferValueComboOffer(scheme.comboOffer, offer)
    cleanOfferValueExcludedChoices(scheme.anyItem, menuBlob)
    cleanOfferValueExcludedChoices(scheme.entireOrder, menuBlob)
  })
}

export function cleanOfferValueIndividualGroups (individualGroups, offer, menuBlob) {
  _.forEachRight(_.get(individualGroups, 'itemGroups', []), itemGroup => {
    const groupReference = _.get(offer, 'itemGroups', []).find(it => it.id === itemGroup.itemGroupId)

    if (!groupReference) {
      return individualGroups.itemGroups.splice(individualGroups.itemGroups.indexOf(itemGroup), 1)
    }

    let departmentId = getItemGroupSingleDepartmentId(groupReference)

    if (!departmentId) {
      itemGroup.value.differentSizeId = null

      if (itemGroup.value.type === 'DIFFERENT_SIZE') {
        itemGroup.value.type = 'AMOUNT'
      }
    } else {
      let styleOptions = getStyleOptionsByKind(departmentId, true, menuBlob).map(it => it.id)

      if (itemGroup.value.differentSizeId && !styleOptions.includes(itemGroup.value.differentSizeId)) {
        itemGroup.value.differentSizeId = null
      }

      if (!itemGroup.value.differentSizeId && itemGroup.value.type === 'DIFFERENT_SIZE') {
        itemGroup.value.type = 'AMOUNT'
      }
    }
  })
}

export function cleanOfferValueComboOffer (comboOffer, offer) {
  if (_.has(comboOffer, 'itemGroups')) {
    const availableGroups = offer.itemGroups.map(it => it.id)

    comboOffer.itemGroups = comboOffer.itemGroups.filter(it => availableGroups.includes(it))
  }
}

export function cleanOfferValueExcludedChoices (parent, menuBlob) {
  if (!_.has(parent, 'excludedChoices')) {
    return
  }

  const departmentIds = _.get(menuBlob, 'departments', []).map(it => it.id)
  let categoryIds = availableCategoriesIds(menuBlob)
  let itemIds = availableItemsIds(menuBlob)

  parent.excludedChoices = parent.excludedChoices.filter(choice => {
    if (!departmentIds.includes(choice.departmentId)) {
      return false
    }

    if (choice.categoryId && !categoryIds.includes(choice.categoryId)) {
      return false
    }

    if (choice.itemId && !itemIds.includes(choice.itemId)) {
      return false
    }

    return true
  })
}

/**
 * Most "clean-X" functions remove unwanted or invalid things, but not this one.
 * This one goes through the list of offers in the menu blob and if any do
 * not have a pricingSchemes entry for the default scheme, then it will be
 * created from another scheme on the offer or from scratch via function below.
 *
 * @param {} menuBlob
 */
export function cleanOfferDefaultPricing (menuBlob) {
  const offers = _.get(menuBlob, 'offers', [])
    .filter(o => o.value)

  offers.forEach(offer => {
    const uniqueSchemes = (offer.value.pricingSchemes || [])
      .filter((scheme, idx, self) => {
        return self.find(s => s.schemeId === scheme.schemeId) === scheme
      })
    offer.value.pricingSchemes = uniqueSchemes
  })

  const pricingSchemes = _.get(menuBlob, 'pricingSchemes', [])
  const defaultPricingScheme = pricingSchemes.find(s => s.isDefault)

  if (!defaultPricingScheme) {
    return
  }

  const offersMissingDefault = offers
    .filter(offer => offer.value && offer.value.pricingSchemes &&
      !offer.value.pricingSchemes.find(s => s.schemeId === defaultPricingScheme.id))

  offersMissingDefault.forEach(offer => {
    const useScheme = (offer.value.pricingSchemes.length && offer.value.pricingSchemes[0]) || emptyOfferPricingScheme()
    const newScheme = JSON.parse(JSON.stringify(useScheme))
    newScheme.schemeId = defaultPricingScheme.id
    offer.value.pricingSchemes.push(newScheme)
  })
}

function emptyOfferPricingScheme () {
  return {
    entityType: 'OFFER_VALUE_PRICING_SCHEME',
    schemeId: '1',
    $identifier: 'schemeId',
    entireOrder: {
      entityType: 'OFFER_VALUE_ENTIRE_ORDER',
      value: {
        entityType: 'OFFER_VALUE_ENTIRE_ORDER_VALUE',
        type: 'AMOUNT',
        amountValue: '0.00',
        percentageValue: '0.00'
      },
      cashierOverride: {
        enabled: true,
        limitOverride: false,
        amountValue: '1.00',
        percentageValue: '1.00'
      },
      freeDelivery: false,
      limitOfferValue: {
        enabled: false,
        amount: '1.00'
      },
      excludedChoices: []
    },
    individualGroups: {
      entityType: 'OFFER_VALUE_INDIVIDUAL_GROUPS',
      itemGroups: [],
      freeDelivery: false,
      limitOfferValue: {
        enabled: false,
        amount: '1.00'
      }
    },
    comboOffer: {
      entityType: 'OFFER_VALUE_COMBO',
      itemGroups: [],
      value: {
        type: 'AMOUNT',
        amountValue: '0.00',
        percentageValue: '0.00',
        fixedPrice: null
      },
      excludeStyleUpcharge: false,
      excludeModifierUpcharges: false,
      limitOfferValue: {
        enabled: false,
        amount: '1.00'
      },
      freeDelivery: false
    },
    anyItem: {
      entityType: 'OFFER_VALUE_ANY_ITEM',
      value: {
        entityType: 'OFFER_VALUE_ANY_ITEM_VALUE',
        type: 'AMOUNT',
        amountValue: '0.00',
        percentageValue: '0.00'
      },
      cashierOverride: {
        enabled: false,
        limitOverride: false,
        amountValue: '1.00',
        percentageValue: '1.00'
      },
      freeDelivery: false,
      limitOfferValue: {
        enabled: false,
        amount: '1.00'
      },
      excludedChoices: []
    }
  }
}
